import * as React from 'react';
import * as ReactDOM from 'react-dom';
import { getFirstFocusable, isElementTabbable } from 'office-ui-fabric-react/lib/Utilities';
import { trace } from 'owa-trace';

/**
 * Marks the given content as a skip link region for the skip link control to provide links to. Will detect whether the given content
 * is a DOM element or a react component to appropriately inject the properties
 * @param skipLinkName the name of the skip link. string will display in control
 * @param role the aria landmark role to assign
 * @param content the element to mark as a skip link
 * @param regionName the name of the region. read when navigating to region, not to child control
 * @param elementRef optional ref to ferry to the element
 */
export function addSkipLinkRegion(
    skipLinkName: string,
    role: 'navigation' | 'main' | 'complementary',
    content: JSX.Element,
    regionName?: string,
    elementRef?: (el: React.ReactInstance) => void
): JSX.Element {
    /**
     * If we are passed a React Component as child content, we can't just set the custom dom attributes directly as props using
     * React.cloneElement(content, {...props}), because that sets the props on the Component, not on the outer DOM element.
     *
     * Instead, we use a ref callback to get the reference to the component itself, then use ReactDOM utilities to get the actual DOM element
     * so we can set our custom attributes (this will override any child attributes)
     */

    // react components have types of functions, but HTMLElements have types of strings
    // https://stackoverflow.com/questions/33199959/how-to-detect-a-react-component-vs-a-react-element

    if (React.isValidElement(content) && typeof content.type === 'function') {
        return React.cloneElement(content as React.ReactElement<any>, {
            ref: el => {
                if (!el) {
                    trace.info('Skip link set to null for: ' + skipLinkName);
                    return;
                }
                let div = ReactDOM.findDOMNode(el) as HTMLElement;
                div.setAttribute('role', role);
                if (!isElementTabbable(div) && div.getAttribute('tabIndex') == null) {
                    // if the element isn't already tabbable, at least make it focusable
                    div.setAttribute('tabIndex', '-1');
                }
                if (regionName) {
                    div.setAttribute('aria-label', regionName);
                }
                div.setAttribute('data-skip-link-name', skipLinkName);
                trace.info('Skip link set for: ' + skipLinkName);

                // if a ref function was passed in, we already have the ref from the element since we're in it's
                // reference function so we can just called the passed function directly rather than passing as a prop
                if (elementRef) {
                    elementRef(el);
                }
            },
        });
    } else {
        return React.cloneElement(content, {
            role: role,
            tabIndex: -1,
            ...(regionName ? { 'aria-label': regionName } : {}),
            'data-skip-link-name': skipLinkName,
            ref: elementRef,
        });
    }
}

/**
 * Marks the first focusable child of the given content as a skip link region for the skip link control to provide links to. Will detect whether the given content
 * is a DOM element or a react component to appropriately inject the properties
 * @param skipLinkName the name of the skip link. string will display in control
 * @param role the aria landmark role to assign
 * @param content the element to mark as a skip link
 * @param regionName the name of the region. read when navigating to region, not to child control
 * @param elementRef optional ref to ferry to the element
 */
export function addSkipLinkRegionToFirstFocusableChild(
    skipLinkName: string,
    role: 'navigation' | 'main' | 'complementary' | null,
    content: JSX.Element,
    regionName?: string,
    elementRef?: (el: React.ReactInstance) => void
): JSX.Element {
    return React.cloneElement(content, {
        ref: el => {
            let domElement = ReactDOM.findDOMNode(el) as HTMLElement;

            // we want to set the role on the parent element so it doesn't interfere with narrator reading default roles of buttons and dropdowns
            if (role && domElement) {
                domElement.setAttribute('role', role);
                if (regionName) {
                    domElement.setAttribute('aria-label', regionName);
                }
            }

            let firstFocusable = getFirstFocusable(
                domElement,
                domElement,
                true /*includeElementsInFocusZones*/
            );
            if (!firstFocusable) {
                trace.info('Skip link set to null for: ' + skipLinkName);
            } else {
                firstFocusable.setAttribute('data-skip-link-name', skipLinkName);
                trace.info('Skip link set for: ' + skipLinkName);
            }

            // if a ref function was passed in, we already have the ref from the element since we're in it's
            // reference function so we can just called the passed function directly rather than passing as a prop
            if (elementRef) {
                elementRef(el);
            }
        },
    });
}
